import { Component, Injector, OnInit, Input } from '@angular/core';
import { Address } from 'app/core/interfaces/adress';
import { genderTranslated } from 'app/core/interfaces/currentUser';
import { Patient } from 'app/core/interfaces/patient';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PatientService } from 'app/core/http/services/patient.service';
import { CognitoService } from 'app/core/http/services/cognito.service';
import { switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { validateImageFile } from 'app/core/validators/validateImageFile';
import { ageValidator } from 'app/core/validators/AgeValidator';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { cpfValidator } from 'app/core/validators/CpfValidator';

@Component({
  selector: 'page-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent extends BaseComponent implements OnInit {
  public address: Address = <Address>{};
  public titles = ['Meus dados', 'Endereço'];
  public genders: genderTranslated[] = [];
  public patientNumber = 0;
  public patient: Patient = <Patient>{};
  public validForm = true;
  public formProfilePatient: FormGroup;
  public userEmail: string = '';
  isSaving = false;

  @Input() mode = 'CREATE';

  profilePictureUrl: string | ArrayBuffer | null = null;
  selectedFileName: string | null = null;

  public selectedTabIndex = 0;
  public alerts: string[] = [];

  constructor(
    private patientService: PatientService,
    private injector: Injector,
    private cognitoService: CognitoService,
  ) {
    super(injector);

    this.formProfilePatient = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-zÀ-ÿ\s]+$/)]),
      cpf: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{11}$/), cpfValidator]),
      mobile: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{11}$/)]),
      email: new FormControl(this.userEmail),
      birthDate: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{8}$/), ageValidator(18, 120)]),
      gender: new FormControl('', [Validators.required]),
      active: new FormControl(true),
      photo: new FormControl(''),
    });
  }

  async ngOnInit() {
    this.showErrorMessage = false;
    this.showSuccessMessage = false;
    this.genders = this.layout.initTranslatedgender();
    await this.loadModeAndData();
    if (this.mode === 'UPDATE') {
      this.getPatient();
    }

    if (this.checkIfIsFirstAccess()) {
      this.evaluateAlerts();
    }
  }

  setPatient(patient: Patient) {
    this.patient = patient;
  }

  ngOnChanges() {
    this.evaluateAlerts();
  }

  evaluateAlerts() {
    this.alerts = [];

    if (this.checkIfIsFirstAccess()) {
      this.alerts = ['📌 Complete seu <strong>perfil</strong>', '⏰ Defina sua <strong>Localização</strong>'];

      this.messageWarning = `
        <h4>Olá, seja bem-vindo(a). Para uma melhor experiência:</h4>
        <ul>
          ${this.alerts.map((a) => `<li>${a}</li>`).join('')}
        </ul>
      `;

      return;
    }

    if (this.objectIsEmpty(this.patient?.address)) {
      this.alerts.push('⏰ Defina sua <strong>Localidade</strong>');
    }

    console.log('alertas', this.alerts);

    if (this.alerts.length > 0) {
      this.messageWarning = `
        <h4>Olá, seja bem-vindo(a). Para uma melhor experiência:</h4>
        <ul>
          ${this.alerts.map((a) => `<li>${a}</li>`).join('')}
        </ul>
      `;
      this.showWarningMessage = true;
    } else {
      this.showWarningMessage = false;
      this.messageWarning = '';
    }
  }

  onTabChange(event: MatTabChangeEvent): void {
    const tabName = this.getTabNameByIndex(event.index);

    this.router.navigate([], {
      queryParams: { tab: tabName },
      queryParamsHandling: 'merge',
    });
  }

  getTabNameByIndex(index: number): string {
    switch (index) {
      case 0:
        return 'perfil';
      case 1:
        return 'localizacao';
      default:
        return 'perfil';
    }
  }

  getTabIndexByName(tabName: string): number {
    switch (tabName) {
      case 'perfil':
        return 0;
      case 'localizacao':
        return 1;
      default:
        return 0;
    }
  }

  private setTabIfAllowed(index: number): void {
    if (this.isTabAllowed(index)) {
      this.selectedTabIndex = index;
    }
  }

  private isTabAllowed(index: number): boolean {
    return !(this.checkIfIsFirstAccess() && index > 0);
  }

  private async loadModeAndData() {
    const { standardAttributes, customAttributes } = await this.cognitoService.getUserAttributes();
    this.userEmail = standardAttributes['email'];
    this.formProfilePatient.patchValue({ email: this.userEmail });
    localStorage.setItem('isFirstAccess', customAttributes['firstAccess']);
    this.mode = localStorage.getItem('isFirstAccess') === 'true' ? 'CREATE' : 'UPDATE';
  }

  private getPatient() {
    const patientId = this.getUserLogged().id;
    this.patientService.showNew(patientId).subscribe({
      next: (patient) => {
        this.patient = patient;

        let formattedMobile = patient.mobile || '';
        if (formattedMobile.startsWith('55')) {
          formattedMobile = formattedMobile.replace('55', '');
        }

        this.formProfilePatient.patchValue({
          name: patient.name,
          birthDate: patient.birthDate,
          cpf: patient.cpf,
          gender: patient.gender,
          mobile: formattedMobile,
          email: this.userEmail,
        });

        this.profilePictureUrl = patient.photo || this.defaultPatientPhoto;
      },
    });
  }

  allowOnlyLetters(event: KeyboardEvent) {
    const char = event.key;
    if (!/^[a-zA-ZÀ-ÿ\s]$/.test(char)) {
      event.preventDefault();
    }
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    const photoControl = this.formProfilePatient.controls['photo'];

    if (!file) {
      return;
    }

    const validationResult = validateImageFile(file, 4);

    if (!validationResult.isValid) {
      photoControl.setErrors(validationResult.errors);
      this.profilePictureUrl = null;
      this.selectedFileName = null;

      return;
    }

    photoControl.setErrors(null);
    this.selectedFileName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        const base64 = reader.result as string;
        this.profilePictureUrl = base64;
        this.formProfilePatient.controls['photo'].setValue(base64);
      }
    };
    reader.readAsDataURL(file);
  }

  onSave(): void {
    this.showErrorMessage = false;
    this.showSuccessMessage = false;
    this.formProfilePatient.markAllAsTouched();

    const errors = this.collectErrors();
    if (errors.length) {
      this.messageError = errors.join('\n');
      this.showErrorMessage = true;
      return;
    }

    this.isSaving = true;

    const payload = this.formProfilePatient.getRawValue();
    if (this.mode === 'CREATE') {
      this.patientService
        .createNew(payload)
        .pipe(
          switchMap((patient: any) => {
            this.auth.setUser(patient, localStorage.getItem('userType'), localStorage.getItem('token'), localStorage.getItem('token_id'));
            this.patient = patient;
            this.mode == 'UPDATE';
            return this.cognitoService.updateFirstAccess();
          }),
        )
        .subscribe({
          next: () => {
            this.showSuccessMessage = true;
            this.isSaving = false;

            if (this.selectedTabIndex == 0) {
              this.selectedTabIndex = this.getTabIndexByName('localizacao');
            }

            this.evaluateAlerts();
          },
          error: (error: HttpErrorResponse) => {
            console.log(error);
            this.showErrorMessage = true;
            this.isSaving = false;

            if (error.status == 413) {
              this.messageError = 'Imagem enviada ultrapassa o tamanho máximo permitido de 4MB';
              return;
            }

            if (error.status == 422) {
              this.messageError = error.error.message;
              return;
            }

            this.messageError = error.message;
          },
        });
    } else {
      const patientId = this.getUserLogged().id;
      const updated = { ...this.patient, ...payload };
      this.patientService.updateNew(patientId, updated).subscribe({
        next: () => {
          this.showSuccessMessage = true;
          this.isSaving = false;

          this.evaluateAlerts();
        },
        error: (error: HttpErrorResponse) => {
          this.showErrorMessage = true;
          this.isSaving = false;

          if (error.status == 413) {
            this.messageError = 'Imagem enviada ultrapassa o tamanho máximo permitido de 4MB';
            return;
          }

          if (error.status == 422) {
            this.messageError = error.error.message;
            return;
          }

          this.messageError = error.message;
        },
      });
    }
  }

  onPasteName(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData?.getData('text') ?? '';
    const validNameRegex = /^[A-Za-zÀ-Üà-ü\s]+$/;

    if (!validNameRegex.test(clipboardData)) {
      event.preventDefault();
    }
  }

  private collectErrors(): string[] {
    const ctrls: any = this.formProfilePatient.controls;
    const errors: string[] = [];

    const requiredFields: string[] = [];
    if (ctrls.name.hasError('required')) {
      requiredFields.push('Nome');
    }
    if (ctrls.cpf.hasError('required')) {
      requiredFields.push('CPF');
    }

    if (ctrls.mobile.hasError('required')) {
      requiredFields.push('Celular');
    }
    if (ctrls.birthDate.hasError('required')) {
      requiredFields.push('Data de nascimento');
    }
    if (ctrls.gender.hasError('required')) {
      requiredFields.push('Gênero');
    }
    if (requiredFields.length) {
      const lista = requiredFields.map((f) => `'${f}'`).join(', ');
      errors.push(`O campo: ${lista} é obrigatório`);
    }

    if (ctrls.name.hasError('pattern')) {
      errors.push('Nome deve conter apenas letras');
    }
    if (ctrls.cpf.hasError('pattern')) {
      errors.push('CPF inválido');
    }

    if (ctrls.cpf.hasError('cpfInvalid')) {
      requiredFields.push('Informe um CPF válido.');
    }
    if (ctrls.mobile.hasError('pattern')) {
      errors.push('Celular deve estar no formato (00) 00000-0000');
    }
    if (ctrls.birthDate.hasError('pattern')) {
      errors.push('Data de nascimento deve estar no formato DD/MM/AAAA');
    }

    if (ctrls.birthDate.hasError('underAge')) {
      errors.push('É preciso ser maior de idade');
    }

    if (ctrls.birthDate.hasError('overAge')) {
      errors.push('Entre com uma idade realista (Idade não pode exceder 120 anos).');
    }

    return errors;
  }

  checkIfIsFirstAccess() {
    const isFirstAccess = localStorage.getItem('isFirstAccess') === 'true';

    if (isFirstAccess) {
      this.showWarningMessage = true;
    }

    return isFirstAccess;
  }

  objectIsEmpty(obj?: any): boolean {
    if (obj == null) return true;

    if (Array.isArray(obj)) {
      return obj.length === 0;
    }

    if (typeof obj === 'object') {
      const values = Object.values(obj);
      return values.every((v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === ''));
    }

    return false;
  }
}
